import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import dig from 'object-dig';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';

import People from '@material-ui/icons/People';
import Input from '@material-ui/core/Input';
import Collapse from '@material-ui/core/Collapse';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Checkbox from '@material-ui/core/Checkbox';

import InputSelect from '~/components/Input/InputSelect';
import InputPhone from '~/components/Input/InputPhone';
import InputRegex from '~/components/Input/InputRegex';
import Paper from '~/components/Paper';
import Form from '~/containers/Form';
import GridItem from '~/components/GridItem';
import GridContainer from '~/components/GridContainer';
import InputControl from '~/components/Input/InputControl';
import InputSelectAsync from '~/components/Input/InputSelectAsync';
import InputCpfCnpj from '~/components/Input/InputCpfCnpj';
import InputDate from '~/components/Input/InputDate';
import InputNumber from '~/components/Input/InputNumber';

import fetchPartners from '~/utils/fetchPartners';
import fetchCorporatePartner from '~/utils/fetchCorporatePartner';
import fetchPartnerSupplier from '~/utils/fetchPartnerSupplier';
import fetchPartnerPromo from '~/utils/fetchPartnerPromo';
import { fetchUsers } from '~/utils/fetchUsers';
import { fetchCities } from '~/utils/fetchCities';
import isBlank from '~/utils/isBlank';
import onlyNumbers from '~/utils/Number/onlyNumbers';

import I18n from '~/i18n';
import countriesEnum from '~/enums/countriesEnum';
import PersonRecord from '~/records/PersonRecord';
import Request from '~/providers/Request';
import Can from '~/contexts/Can';
import { genresOptions } from '~/config/constants';

import useStyles from './styles';
import { Typography } from '@material-ui/core';

const attrName = (attr) => PersonRecord.hummanAttributeName(attr);
const trExtra = (extra) => I18n.t(`person.extras.${extra}`);

const CONSELHO_OPTIONS = [
  { id: 'CRM', name: 'CRM' },
  { id: 'CRO', name: 'CRO' },
  { id: 'CRMV', name: 'CRMV' },
  { id: 'CRF', name: 'CRF' },
];

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
].map((uf) => ({ id: uf, name: uf }));

function ClientForm({
  dependentOwner,
  loading,
  record,
  onSubmit,
  handleChange,
  setRecord,
  title,
}) {
  const classes = useStyles();
  const isNew = isBlank(record.get('id'));
  const handleRadio = (event) =>
    setRecord(record.updateAttributes({ role: event.target.value }));
  const [specialtyOptions, setSpecialtyOptions] = useState(
    record.get('specialties')
  );
  const [requiredAttributes, setRequiredAttributes] = useState();

  useEffect(() => {
    fetchSpecialties();
  }, []);

  const fetchCompanyRequiredAttributes = (id) => {
    Request.get(`/admin/corporation/${id}`, null, { normalizeJSON: false })
      .then((resp) => {
        const config_corporations = resp.included?.find(
          (item) => item.type === 'config_corporations'
        );

        if (config_corporations) {
          setRequiredAttributes(config_corporations.attributes);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    const corporatePartner = record.get('corporate_partner');
    if (corporatePartner) {
      fetchCompanyRequiredAttributes(corporatePartner.corporation_id);
    } else {
      setRequiredAttributes({});
    }
  }, [record.get('corporate_partner')?.corporation_id]);

  const fetchSpecialties = () => {
    Request.get('/admin/specialties', null, { normalizeJSON: false })
      .then((resp) => {
        const items = resp.data
          .filter((e) => e.attributes && e.attributes.is_active === true)
          .map((e) => ({
            id: Number(e.id),
            name: `${e.attributes.name}`,
            appointment_duration: e.attributes.appointment_duration,
          }));
        setSpecialtyOptions(items);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  async function handleChangeRadioBenefitsClub(e) {
    setRecord(
      record.updateAttributes({
        has_benefits_club: e.target.value === 'true' ? true : false,
      })
    );
  }

  const handleChangeRegulator = (e) =>
    setRecord(record.updateAttributes({ is_regulator: e.target.checked }));

  const handleChangeReportsOnly = (e) =>
    setRecord(record.updateAttributes({ reports_only: e.target.checked }));

  useEffect(() => {
    async function getCep() {
      const cep = onlyNumbers(dig(record.get('address'), 'postal_code'));
      const response = await fetch(
        `https://brasilapi.com.br/api/cep/v1/${cep}`
      );
      if (response.status !== 200) {
        toast.error(I18n.t('person.toast.cep_not_found'));
      }
      let data = {};
      try {
        data = await response.json();
        await fetchCities(data.city, 0, (response) => {
          const city = response.items.filter(
            (key) => key.name === `${data.city} - ${data.state}`
          );
          setRecord(
            record.updateAttributes({
              address: {
                ...record.get('address'),
                city: city[0],
                neighborhood: data.neighborhood,
                street: data.street,
              },
            })
          );
        });
      } catch (error) {
        console.log(error);
      }
    }
    if (dig(record.get('address'), 'postal_code')?.length === 9) {
      getCep();
    }
  }, [dig(record.get('address'), 'postal_code')]);
  return (
    <>
      <Form onSubmit={onSubmit}>
        <Paper
          Icon={People}
          classes={classes.userInfoCard}
          title={trExtra(`${title}`)}
          loading={loading}
        >
          <div className={clsx(classes.root)}>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  label={attrName('name')}
                  value={record.get('name')}
                  name={'name'}
                  required={true}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  label={attrName('email')}
                  value={record.get('email')}
                  name={'email'}
                  required={
                    requiredAttributes?.required_email === false ? false : true
                  }
                  inputComponent={Input}
                  onChange={handleChange}
                  InputProps={{
                    inputProps: {
                      autoComplete: 'off',
                    },
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  label={attrName('cell_phone')}
                  value={record.get('cell_phone')}
                  name={'cell_phone'}
                  inputComponent={InputPhone}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} md={3}>
                <InputControl
                  label={attrName('cpf')}
                  value={record.get('cpf')}
                  name={'cpf'}
                  required={true}
                  inputComponent={InputCpfCnpj}
                  onChange={handleChange}
                  InputProps={{
                    force: 'cpf',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={5}>
                <InputControl
                  label={attrName('reason_to_use')}
                  value={record.get('reason_to_use')}
                  inputComponent={InputSelect}
                  name={'reason_to_use'}
                  onChange={({ reason_to_use }) => {
                    setRecord(record.updateAttributes({ reason_to_use }));
                  }}
                  InputProps={{
                    options: [
                      {
                        id: 'op_001',
                        name: 'Sou mãe/pai e quero cuidar dos meus filhos.',
                      },
                      { id: 'op_002', name: 'Quero cuidar de quem eu gosto.' },
                      {
                        id: 'op_003',
                        name: 'Sou filho e vou participar do app.',
                      },
                      {
                        id: 'op_004',
                        name: 'Tenho mais de 60 anos e vou participar do app.',
                      },
                      { id: 'op_005', name: 'Quero a Orientação Médica.' },
                      {
                        id: 'op_006',
                        name: 'Sou gestor e quero a Direção Inteligente na frota.',
                      },
                      {
                        id: 'op_007',
                        name: 'Sou motorista de caminhão e vou participar do app.',
                      },
                      {
                        id: 'op_008',
                        name: 'Tenho interesse no Botão de Pânico SOS.',
                      },
                      { id: 'op_009', name: 'Outros.' },
                      {
                        id: 'op_010',
                        name: 'Sou colaborador / dependente de uma empresa',
                      },
                    ],
                    outValueType: 'string',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  label={attrName('nationality')}
                  value={record.get('nationality')}
                  name={'nationality'}
                  inputComponent={InputSelect}
                  onChange={handleChange}
                  InputProps={{
                    options: countriesEnum().array,
                    outValueType: 'string',
                  }}
                />
              </GridItem>
            </GridContainer>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  name={'partner'}
                  label={attrName('partner')}
                  value={record.get('partner')}
                  inputComponent={InputSelectAsync}
                  onChange={handleChange}
                  InputProps={{
                    isClearable: true,
                    handleFetch: fetchPartnerPromo,
                    createEnabled: false,
                    error: false,
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  name={'partner_supplier'}
                  label={attrName('partner_supplier')}
                  value={record.get('partner_supplier')}
                  inputComponent={InputSelectAsync}
                  onChange={handleChange}
                  InputProps={{
                    isClearable: true,
                    handleFetch: fetchPartnerSupplier,
                    createEnabled: false,
                    error: false,
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  name={'corporate_partner'}
                  label={attrName('corporate_partner')}
                  value={record.get('corporate_partner')}
                  inputComponent={InputSelectAsync}
                  onChange={handleChange}
                  InputProps={{
                    isClearable: true,
                    handleFetch: fetchCorporatePartner,
                    createEnabled: false,
                    error: false,
                  }}
                />
              </GridItem>
            </GridContainer>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  label={attrName('owner_partner')}
                  value={record.get('owner_partner')}
                  name={'owner_partner'}
                  inputComponent={InputSelectAsync}
                  onChange={handleChange}
                  InputProps={{
                    isClearable: true,
                    handleFetch: fetchPartners,
                    createEnabled: false,
                    error: false,
                  }}
                />
              </GridItem>
              <GridItem
                xs={12}
                md={3}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      name="is_regulator"
                      checked={!!record.get('is_regulator')}
                      onChange={handleChangeRegulator}
                    />
                  }
                  label={attrName('is_regulator')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      name="reports_only"
                      checked={!!record.get('reports_only')}
                      onChange={handleChangeReportsOnly}
                    />
                  }
                  label={attrName('reports_only')}
                  style={{ whiteSpace: 'nowrap' }}
                />
              </GridItem>
              <GridItem xs={12} md={2}>
                <InputControl
                  InputProps={{
                    type: 'password',
                    inputProps: {
                      autoComplete: 'new-password',
                    },
                  }}
                  required={isNew}
                  label={'Senha'}
                  name={'password'}
                  value={record.get('password')}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} md={3}>
                <InputControl
                  value={moment(record.get('expires_at')).format()}
                  name="expires_at"
                  label={attrName('expires_at')}
                  inputComponent={InputDate}
                  InputProps={{
                    type: 'datetime',
                    minDate: moment(),
                  }}
                  onChange={(date) =>
                    handleChange({ expires_at: moment(date).format() })
                  }
                />
              </GridItem>
            </GridContainer>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  value={moment(record.get('born_date')).format()}
                  name="born_date"
                  label={attrName('born_date')}
                  inputComponent={InputDate}
                  InputProps={{
                    type: 'date',
                  }}
                  onChange={(date) =>
                    handleChange({ born_date: moment(date).format() })
                  }
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <InputControl
                  label={attrName('gender')}
                  value={record.get('gender')}
                  inputComponent={InputSelect}
                  name={'gender'}
                  onChange={({ gender }) => {
                    setRecord(record.updateAttributes({ gender }));
                  }}
                  InputProps={{
                    isClearable: true,
                    outValueType: 'string',
                    options: Object.keys(genresOptions).map((key) => ({
                      id: key,
                      name: genresOptions[key],
                    })),
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={1}>
                <InputControl
                  label={attrName('cns')}
                  value={record.get('cns')}
                  inputComponent={InputRegex}
                  name={'cns'}
                  InputProps={{
                    mask: [
                      /[1-9]/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                    ],
                  }}
                  onChange={({ cns }) => {
                    if (cns && cns.length > 15) return;
                    setRecord(record.updateAttributes({ cns }));
                  }}
                />
              </GridItem>

              <GridItem style={{ marginTop: '5px' }} xs={12} md={3}>
                <Typography
                  style={{
                    fontSize: '13px',
                    color: '#505050',
                    fontWeight: '500',
                  }}
                >
                  {attrName('has_benefits_club')}
                </Typography>
                <RadioGroup
                  name="has_benefits_club"
                  value={record.get('has_benefits_club')}
                  aria-label="has_benefits_club"
                  onChange={handleChangeRadioBenefitsClub}
                  row
                >
                  <FormControlLabel
                    value={true}
                    control={
                      <Radio
                        color="primary"
                        disabled={!record.get('enabled_benefits_club')}
                      />
                    }
                    label="SIM"
                  />
                  <FormControlLabel
                    value={false}
                    control={
                      <Radio
                        color="primary"
                        disabled={!record.get('enabled_benefits_club')}
                      />
                    }
                    label="NÃO"
                  />
                </RadioGroup>
              </GridItem>

              {isNew && (
                <GridItem xs={12} md={4}>
                  <InputControl
                    name={'dependentOwner'}
                    label={'Dependente do usuário'}
                    value={dependentOwner}
                    inputComponent={InputSelectAsync}
                    onChange={handleChange}
                    InputProps={{
                      isClearable: true,
                      handleFetch: fetchUsers,
                      createEnabled: false,
                      error: false,
                    }}
                  />
                </GridItem>
              )}
            </GridContainer>
            <GridContainer>
              <GridItem xs={6}>
                <InputControl
                  value={record.get('device_info')}
                  name={'device_info'}
                  label={attrName('device_info')}
                  inputComponent={Input}
                  InputProps={{
                    multiline: true,
                    rows: '4',
                  }}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={6}>
                <InputControl
                  value={record.get('signup_messages')}
                  name={'signup_messages'}
                  label={attrName('signup_messages')}
                  inputComponent={Input}
                  InputProps={{
                    multiline: true,
                    rows: '4',
                  }}
                  onChange={handleChange}
                />
              </GridItem>
            </GridContainer>
            <Can I="edit" a="Peoples" field="role">
              <GridContainer>
                <GridItem xs={4}>
                  <RadioGroup
                    aria-label="position"
                    name="position"
                    value={record.get('role')}
                    onChange={handleRadio}
                  >
                    <FormControlLabel
                      value="customer"
                      control={<Radio color="primary" />}
                      label={attrName('customer')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="admin"
                      control={<Radio color="primary" />}
                      label={attrName('admin')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="partner"
                      control={<Radio color="primary" />}
                      label={attrName('partner_role')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="doctor"
                      control={<Radio color="primary" />}
                      label={attrName('doctor')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="attendant"
                      control={<Radio color="primary" />}
                      label={attrName('attendant')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="marketing"
                      control={<Radio color="primary" />}
                      label={attrName('marketing')}
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </GridItem>
                <GridItem xs={8}>
                  <Collapse in={record.get('role') === 'doctor'}>
                    <InputControl
                      name={'specialties'}
                      label={attrName('doctor_specialties')}
                      value={record.get('specialties')}
                      inputComponent={InputSelect}
                      onChange={({ specialties }) => {
                        const previousIds = (record.get('specialties') || []).map(
                          (specialty) => String(specialty.id)
                        );
                        const durations = { ...record.get('specialty_durations') };
                        (specialties || [])
                          .filter((specialty) => !previousIds.includes(String(specialty.id)))
                          .forEach((specialty) => {
                            if (isBlank(durations[specialty.id]))
                              durations[specialty.id] = specialty.appointment_duration;
                          });
                        setRecord(
                          record.updateAttributes({
                            specialties: specialties,
                            specialty_durations: durations,
                          })
                        );
                      }}
                      InputProps={{
                        isClearable: true,
                        isMulti: true,
                        options: specialtyOptions,
                      }}
                    />
                    {!isBlank(record.get('specialties')) && (
                      <GridContainer>
                        <GridItem xs={12} md={12}>
                          <Typography style={{ fontSize: '12px', marginTop: 24 }}>
                            Duração da consulta por especialidade
                          </Typography>
                        </GridItem>
                        {record.get('specialties').map((specialty) => (
                          <React.Fragment key={specialty.id}>
                            <GridItem xs={5} md={5}>
                              <InputControl
                                label={'Especialidade'}
                                value={specialty.name}
                                name={'specialty_name'}
                                inputComponent={Input}
                                onChange={() => {}}
                                InputProps={{ readOnly: true }}
                              />
                            </GridItem>
                            <GridItem xs={3} md={3}>
                              <InputControl
                                required
                                label={'Duração da consulta (min)'}
                                value={
                                  (record.get('specialty_durations') || {})[
                                    specialty.id
                                  ]
                                }
                                name={'appointment_duration'}
                                inputComponent={InputNumber}
                                onChange={({ appointment_duration }) => {
                                  setRecord(
                                    record.updateAttributes({
                                      specialty_durations: {
                                        ...record.get('specialty_durations'),
                                        [specialty.id]: appointment_duration,
                                      },
                                    })
                                  );
                                }}
                                InputProps={{
                                  precision: 0,
                                  showMask: false,
                                  config: { allowNegative: false },
                                }}
                              />
                            </GridItem>
                          </React.Fragment>
                        ))}
                      </GridContainer>
                    )}
                    <GridContainer>
                    <GridItem xs={12} md={12}>
                      <Typography
                        style={{
                          fontSize: '12px',
                          color: '#1976d2',
                          fontWeight: 500,
                          marginTop: 8,
                        }}
                      >
                        Registro profissional
                      </Typography>
                    </GridItem>
                    <GridItem xs={6} md={6}>
                      <InputControl
                        label={'Número'}
                        value={record.get('crm_numero')}
                        name={'crm_numero'}
                        inputComponent={Input}
                        onChange={handleChange}
                      />
                    </GridItem>
                    <GridItem xs={6} md={6}>
                      <InputControl
                        label={'Conselho'}
                        value={record.get('crm_conselho')}
                        name={'crm_conselho'}
                        inputComponent={InputSelect}
                        onChange={({ crm_conselho }) => {
                          setRecord(
                            record.updateAttributes({ crm_conselho })
                          );
                        }}
                        InputProps={{
                          isClearable: true,
                          options: CONSELHO_OPTIONS,
                          outValueType: 'string',
                        }}
                      />
                    </GridItem>
                    <GridItem xs={6} md={6}>
                      <InputControl
                        label={'UF'}
                        value={record.get('crm_uf')}
                        name={'crm_uf'}
                        inputComponent={InputSelect}
                        onChange={({ crm_uf }) => {
                          setRecord(record.updateAttributes({ crm_uf }));
                        }}
                        InputProps={{
                          isClearable: true,
                          options: UF_OPTIONS,
                          outValueType: 'string',
                        }}
                      />
                    </GridItem>
                    </GridContainer>
                  </Collapse>
                </GridItem>
              </GridContainer>
            </Can>
            <GridContainer>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  label={attrName('postal_code')}
                  value={dig(record.get('address'), 'postal_code')}
                  name={'postal_code'}
                  inputComponent={InputRegex}
                  onChange={(event) => {
                    setRecord(
                      record.updateAttributes({
                        address: {
                          ...record.get('address'),
                          postal_code: event.postal_code,
                        },
                      })
                    );
                  }}
                  InputProps={{
                    mask: [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/],
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'city'}
                  label={attrName('city')}
                  value={dig(record.get('address'), 'city')}
                  inputComponent={InputSelectAsync}
                  onChange={(event) => {
                    setRecord(
                      record.updateAttributes({
                        address: {
                          ...record.get('address'),
                          city: event.city,
                        },
                      })
                    );
                  }}
                  InputProps={{
                    isClearable: true,
                    handleFetch: fetchCities,
                    createEnabled: false,
                    error: false,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  label={attrName('neighborhood')}
                  value={dig(record.get('address'), 'neighborhood')}
                  name={'neighborhood'}
                  inputComponent={Input}
                  onChange={(event) => {
                    setRecord(
                      record.updateAttributes({
                        address: {
                          ...record.get('address'),
                          neighborhood: event.neighborhood,
                        },
                      })
                    );
                  }}
                />
              </GridItem>
            </GridContainer>
            <GridContainer>
              <GridItem xs={12} sm={10} md={10} lg={10} xl={10}>
                <InputControl
                  label={attrName('street')}
                  value={dig(record.get('address'), 'street')}
                  name={'street'}
                  inputComponent={Input}
                  onChange={(event) => {
                    setRecord(
                      record.updateAttributes({
                        address: {
                          ...record.get('address'),
                          street: event.street,
                        },
                      })
                    );
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
                <InputControl
                  label={attrName('number')}
                  value={dig(record.get('address'), 'number')}
                  name={'number'}
                  inputComponent={Input}
                  onChange={(event) => {
                    setRecord(
                      record.updateAttributes({
                        address: {
                          ...record.get('address'),
                          number: event.number,
                        },
                      })
                    );
                  }}
                />
              </GridItem>
            </GridContainer>
          </div>
        </Paper>
      </Form>
    </>
  );
}

ClientForm.propTypes = {
  loading: PropTypes.bool,
};

export default React.memo(ClientForm);
